import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { fileUrl, formatApiError } from "@/lib/api";
import {
  folders as foldersApi,
  documents as documentsApi,
} from "@/api";
import { toast } from "sonner";
import {
  Folder as FolderIcon,
  FolderOpen,
  FilePdf,
  FileImage,
  FileXls,
  FileDoc,
  FilePpt,
  File as FileIcon,
  UploadSimple,
  FolderPlus,
  Trash,
  ShareNetwork,
  CaretRight,
  CaretLeft,
  CaretDown,
  X,
  Eye,
  MagnifyingGlass,
  DownloadSimple,
  List,
  CheckSquare,
  DotsThreeVertical,
  PencilSimple,
} from "@phosphor-icons/react";
import ShareModal from "@/components/ShareModal";
import AccessModal from "@/components/documents/AccessModal";
import DocumentsMobileList from "@/components/documents/DocumentsMobileList";
import DocumentPreviewModal from "@/components/documents/DocumentPreviewModal";
import { SortableTableHeader } from "@/components/ui/SortableTableHeader";
import { ActaActionMenu, ActaActionMenuItem, ActaActionSheet } from "@/components/ui/ActaActionMenu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useTableSort } from "@/hooks/useTableSort";
import { sortRows } from "@/lib/tableSort";

const PAGE_SIZE = 10;

function iconFor(ext) {
  const e = (ext || "").toLowerCase();
  if (e === "pdf") return FilePdf;
  if (["png", "jpg", "jpeg", "gif", "webp", "svg"].includes(e)) return FileImage;
  if (["xls", "xlsx", "csv"].includes(e)) return FileXls;
  if (["doc", "docx"].includes(e)) return FileDoc;
  if (["ppt", "pptx"].includes(e)) return FilePpt;
  return FileIcon;
}

function humanSize(b) {
  if (!b) return "—";
  const u = ["o", "Ko", "Mo", "Go"];
  let i = 0;
  let n = b;
  while (n >= 1024 && i < u.length - 1) {
    n /= 1024;
    i++;
  }
  return `${n.toFixed(n >= 10 || i === 0 ? 0 : 1)} ${u[i]}`;
}

function buildFolderTree(folders, parentId = null) {
  return folders
    .filter((folder) => (folder.parentId || null) === parentId)
    .sort((a, b) => (a.name || "").localeCompare(b.name || "", "fr"))
    .map((folder) => ({
      ...folder,
      children: buildFolderTree(folders, folder.id),
    }));
}

function collectFolderSubtreeIds(folders, rootFolderId) {
  const childrenByParent = new Map();

  folders.forEach((folder) => {
    const parentId = folder.parentId || null;

    if (!childrenByParent.has(parentId)) {
      childrenByParent.set(parentId, []);
    }

    childrenByParent.get(parentId).push(folder.id);
  });

  const subtreeIds = new Set([rootFolderId]);
  const stack = [rootFolderId];

  while (stack.length > 0) {
    const currentId = stack.pop();

    for (const childId of childrenByParent.get(currentId) || []) {
      if (subtreeIds.has(childId)) continue;

      subtreeIds.add(childId);
      stack.push(childId);
    }
  }

  return subtreeIds;
}

function FolderTreeNode({
  folder,
  level,
  currentFolderId,
  expandedFolderIds,
  onToggle,
  onSelect,
  mobile = false,
}) {
  const hasChildren = folder.children.length > 0;
  const isExpanded = expandedFolderIds.has(folder.id);
  const isActive = currentFolderId === folder.id;

  if (mobile) {
    return (
      <div>
        <div
          className={`flex items-center gap-1 rounded-lg px-2 py-1.5 text-sm ${
            isActive
              ? "bg-[var(--do-accent-soft)] text-[var(--do-accent)]"
              : "text-[var(--do-ink)]"
          }`}
          style={{ paddingLeft: `${8 + level * 14}px` }}
          data-testid={`mobile-folder-tree-${folder.id}`}
        >
          <FolderIcon size={17} weight="duotone" className="shrink-0" />

          <button
            type="button"
            onClick={() => onSelect(folder.id)}
            className="documents-v2-folder-name min-w-0 flex-1 truncate text-left font-heading font-medium"
            data-testid={`mobile-folder-select-${folder.id}`}
          >
            {folder.name}
          </button>

          {hasChildren ? (
            <button
              type="button"
              onClick={() => onToggle(folder.id)}
              className="inline-flex h-7 w-7 shrink-0 items-center justify-center"
              aria-label={isExpanded ? "Réduire le dossier" : "Développer le dossier"}
              aria-expanded={isExpanded}
              data-testid={`mobile-folder-toggle-${folder.id}`}
            >
              <CaretDown
                size={14}
                className={`transition-transform duration-200 ease-out ${
                  isExpanded ? "rotate-0" : "-rotate-90"
                }`}
              />
            </button>
          ) : (
            <span className="h-7 w-7" aria-hidden="true" />
          )}
        </div>

        {hasChildren && (
          <div
            className={`grid transition-[grid-template-rows,opacity] duration-200 ease-out ${
              isExpanded
                ? "grid-rows-[1fr] opacity-100"
                : "grid-rows-[0fr] opacity-0"
            }`}
            aria-hidden={!isExpanded}
            data-testid={`mobile-folder-children-${folder.id}`}
          >
            <div className="min-h-0 overflow-hidden">
              {folder.children.map((child) => (
                <FolderTreeNode
                  key={child.id}
                  folder={child}
                  level={level + 1}
                  currentFolderId={currentFolderId}
                  expandedFolderIds={expandedFolderIds}
                  onToggle={onToggle}
                  onSelect={onSelect}
                  mobile
                />
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      <div
        className={`acta-desktop-context-link ${isActive ? "is-active" : ""}`}
        style={{ paddingLeft: `${12 + level * 14}px` }}
        data-testid={`folder-tree-${folder.id}`}
      >
        <FolderIcon size={18} weight="duotone" />

        <button
          type="button"
          onClick={() => onSelect(folder.id)}
          className="documents-v2-folder-name min-w-0 truncate text-left"
          data-testid={`folder-select-${folder.id}`}
        >
          {folder.name}
        </button>

        {hasChildren ? (
          <button
            type="button"
            onClick={() => onToggle(folder.id)}
            className="inline-flex items-center justify-center opacity-45"
            aria-label={isExpanded ? "Réduire le dossier" : "Développer le dossier"}
            aria-expanded={isExpanded}
            data-testid={`folder-toggle-${folder.id}`}
          >
            <CaretDown
              size={14}
              weight="bold"
              className={`transition-transform duration-200 ease-out ${
                isExpanded ? "rotate-0" : "-rotate-90"
              }`}
            />
          </button>
        ) : (
          <span aria-hidden="true" />
        )}
      </div>

      {hasChildren && (
        <div
          className={`grid transition-[grid-template-rows,opacity] duration-200 ease-out ${
            isExpanded
              ? "grid-rows-[1fr] opacity-100"
              : "grid-rows-[0fr] opacity-0"
          }`}
          aria-hidden={!isExpanded}
          data-testid={`folder-children-${folder.id}`}
        >
          <div className="acta-context-sidebar-list min-h-0 overflow-hidden">
            {folder.children.map((child) => (
              <FolderTreeNode
                key={child.id}
                folder={child}
                level={level + 1}
                currentFolderId={currentFolderId}
                expandedFolderIds={expandedFolderIds}
                onToggle={onToggle}
                onSelect={onSelect}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function Documents() {

  const [currentFolderId, setCurrentFolderId] = useState(null);
  const [folders, setFolders] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);

  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");

  const [documentSearch, setDocumentSearch] = useState("");
  const [
    globalSearchDocuments,
    setGlobalSearchDocuments,
  ] = useState([]);
  const [
    globalSearchLoaded,
    setGlobalSearchLoaded,
  ] = useState(false);

  const normalizedDocumentSearch =
    documentSearch.trim().toLowerCase();

  const globalSearchActive =
    normalizedDocumentSearch.length > 0;

  const activeDocumentSource =
    globalSearchActive
      ? (
          globalSearchLoaded
            ? globalSearchDocuments
            : []
        )
      : documents;

  const [expandedFolderIds, setExpandedFolderIds] = useState(new Set());

  const [selectedDocIds, setSelectedDocIds] = useState(new Set());
  // Keep selected document metadata when navigating to another folder.
  const [selectedDocumentCache, setSelectedDocumentCache] = useState(new Map());
  const [selectedFolderIds, setSelectedFolderIds] = useState(new Set());
  const [page, setPage] = useState(1);
  const [actionMenuId, setActionMenuId] = useState(null);
  const [desktopActionMenu, setDesktopActionMenu] = useState(null);
  const [mobileActionSheet, setMobileActionSheet] = useState(null);

  const {
    sortBy,
    sortOrder,
    toggleSort,
  } = useTableSort("name", "asc");

  const handleDocumentSort = (field) => {
    toggleSort(field);
    setPage(1);
  };

  const [preview, setPreview] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareItem, setShareItem] = useState(null);
  const [accessSelection, setAccessSelection] = useState(null);
  const [moveItem, setMoveItem] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [renameTarget, setRenameTarget] = useState(null);


  const isMobileActionsViewport = () =>
    typeof window !== "undefined" &&
    window.matchMedia("(max-width: 720px)").matches;

  const closeAllActionMenus = () => {
    setActionMenuId(null);
    setDesktopActionMenu(null);
    setMobileActionSheet(null);
  };

  const getDesktopMenuHeight = (payload) => {
    const permissions = payload.item?.permissions || {};
    let actionCount =
      payload.type === "document" ? 1 : 0;

    if (permissions.canManageAccess) {
      actionCount += 1;
    }

    if (permissions.canShare) {
      actionCount += 1;
    }

    if (permissions.canEdit) {
      // Renommer + Déplacer
      actionCount += 2;
    }

    if (permissions.canDelete) {
      actionCount += 1;
    }

    return Math.max(1, actionCount) * 45 + 8;
  };

  const openRowActionMenu = (event, payload) => {
    event.stopPropagation();

    if (isMobileActionsViewport()) {
      setActionMenuId(null);
      setDesktopActionMenu(null);
      setMobileActionSheet(payload);
      return;
    }

    if (actionMenuId === payload.menuId) {
      closeAllActionMenus();
      return;
    }

    const rect =
      event.currentTarget.getBoundingClientRect();

    const menuWidth = 220;
    const menuHeight =
      getDesktopMenuHeight(payload);
    const gap = 8;
    const padding = 12;

    const availableBelow =
      window.innerHeight -
      rect.bottom -
      padding;

    const availableAbove =
      rect.top -
      padding;

    const openAbove =
      availableBelow < menuHeight &&
      availableAbove > availableBelow;

    const left = Math.min(
      window.innerWidth -
        menuWidth -
        padding,
      Math.max(
        padding,
        rect.right - menuWidth
      )
    );

    const requestedTop = openAbove
      ? rect.top - menuHeight - gap
      : rect.bottom + gap;

    const top = Math.min(
      window.innerHeight -
        menuHeight -
        padding,
      Math.max(
        padding,
        requestedTop
      )
    );

    setMobileActionSheet(null);
    setActionMenuId(payload.menuId);
    setDesktopActionMenu({
      ...payload,
      anchorEl: event.currentTarget,
    });
  };

  /**
   * Contrôle l'ouverture du panneau dossiers sur mobile.
   *
   * Sur desktop, le panneau reste visible et redimensionnable.
   */
  const [mobileFoldersOpen, setMobileFoldersOpen] = useState(false);
  const [isDesktopViewport, setIsDesktopViewport] = useState(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia("(min-width: 1024px)").matches
  );
  const [desktopTreeHost, setDesktopTreeHost] = useState(null);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const desktopMedia = window.matchMedia("(min-width: 1024px)");

    const syncDesktopNavigation = () => {
      const isDesktop = desktopMedia.matches;

      setIsDesktopViewport(isDesktop);
      setDesktopTreeHost(
        isDesktop
          ? document.getElementById("acta-documents-desktop-tree-slot")
          : null
      );

      if (isDesktop) {
        setMobileFoldersOpen(false);
      }
    };

    syncDesktopNavigation();
    desktopMedia.addEventListener("change", syncDesktopNavigation);

    return () => {
      desktopMedia.removeEventListener("change", syncDesktopNavigation);
    };
  }, []);

  const fileInputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    setGlobalSearchLoaded(false);

    try {
      const [foldersData, docsData] = await Promise.all([
        foldersApi.list(),
        documentsApi.list(currentFolderId || "root"),
      ]);

      setFolders(foldersData || []);
      setDocuments(docsData || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    setDocumentSearch("");
    setPage(1);
    setMobileFoldersOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFolderId]);

  useEffect(() => {
    setPage(1);

    // Selection persists while filtering and navigating inside Documents.
  }, [documentSearch]);

  useEffect(() => {
    if (
      !globalSearchActive ||
      globalSearchLoaded
    ) {
      return undefined;
    }

    let cancelled = false;

    const timer = window.setTimeout(
      () => {
        documentsApi
          .list()
          .then((items) => {
            if (cancelled) return;

            setGlobalSearchDocuments(
              items || []
            );
            setGlobalSearchLoaded(true);
          })
          .catch((error) => {
            if (cancelled) return;

            toast.error(
              formatApiError(
                error.response?.data?.detail
              ) ||
                error.message ||
                "Recherche impossible"
            );
          });
      },
      180
    );

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [
    documentSearch,
    globalSearchActive,
    globalSearchLoaded,
  ]);

  useEffect(() => {
    const closeActionMenus = (event) => {
      if (
        !event.target.closest(
          "[data-documents-action-menu]"
        ) &&
        !event.target.closest(
          "[data-documents-desktop-action-menu]"
        ) &&
        !event.target.closest(
          "[data-documents-mobile-action-sheet]"
        )
      ) {
        setActionMenuId(null);
        setDesktopActionMenu(null);
      }
    };

    document.addEventListener(
      "click",
      closeActionMenus
    );

    return () => {
      document.removeEventListener(
        "click",
        closeActionMenus
      );
    };
  }, []);

  useEffect(() => {
    if (!desktopActionMenu) {
      return;
    }

    const closeDesktopActionMenu = () => {
      setActionMenuId(null);
      setDesktopActionMenu(null);
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        closeDesktopActionMenu();
      }
    };

    window.addEventListener(
      "resize",
      closeDesktopActionMenu
    );

    window.addEventListener(
      "scroll",
      closeDesktopActionMenu,
      true
    );

    document.addEventListener(
      "keydown",
      handleKeyDown
    );

    return () => {
      window.removeEventListener(
        "resize",
        closeDesktopActionMenu
      );

      window.removeEventListener(
        "scroll",
        closeDesktopActionMenu,
        true
      );

      document.removeEventListener(
        "keydown",
        handleKeyDown
      );
    };
  }, [desktopActionMenu]);

  useEffect(() => {
    if (!mobileActionSheet) {
      document.body.classList.remove(
        "documents-v2-mobile-sheet-open"
      );
      return;
    }

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setMobileActionSheet(null);
      }
    };

    document.body.classList.add(
      "documents-v2-mobile-sheet-open"
    );
    document.addEventListener(
      "keydown",
      handleKeyDown
    );

    return () => {
      document.body.classList.remove(
        "documents-v2-mobile-sheet-open"
      );
      document.removeEventListener(
        "keydown",
        handleKeyDown
      );
    };
  }, [mobileActionSheet]);

  const folderTree = useMemo(
    () => buildFolderTree(folders),
    [folders]
  );

  const breadcrumb = useMemo(() => {
    const chain = [];
    let cur = folders.find((f) => f.id === currentFolderId);

    while (cur) {
      chain.unshift(cur);
      cur = folders.find((f) => f.id === cur.parentId);
    }

    return chain;
  }, [folders, currentFolderId]);

  const childFolders = useMemo(
    () => folders.filter((folder) => (folder.parentId || null) === currentFolderId),
    [folders, currentFolderId]
  );

  const currentFolder = useMemo(
    () => folders.find((folder) => folder.id === currentFolderId) || null,
    [folders, currentFolderId]
  );

  const canAddContent =
    !currentFolderId
    || Boolean(currentFolder?.permissions?.canAddContent);

  /**
   * Sous-dossiers correspondant à la recherche.
   *
   * Les dossiers ne sont volontairement pas paginés :
   * ils restent visibles au-dessus des documents afin que
   * la pagination représente exactement 10 documents.
   */
  const filteredFolderItems = useMemo(() => {
    const folderSource =
      globalSearchActive
        ? folders
        : childFolders;

    const matchingFolders = folderSource
      .map((folder) => ({
        ...folder,
        itemType: "folder",
        extension: "Dossier",
        size: null,
        createdAt: folder.createdAt,
      }))
      .filter((folder) => {
        if (!globalSearchActive) {
          return true;
        }

        return (folder.name || "")
          .toLowerCase()
          .includes(
            normalizedDocumentSearch
          );
      });

    return sortRows(matchingFolders, {
      sortBy,
      sortOrder,
      accessors: {
        name: (item) =>
          item.name || "",
        extension: (item) =>
          item.extension || "Dossier",
        size: () => null,
        createdAt: (item) =>
          item.createdAt
            ? new Date(item.createdAt).getTime()
            : null,
      },
    });
  }, [
    childFolders,
    folders,
    globalSearchActive,
    normalizedDocumentSearch,
    sortBy,
    sortOrder,
  ]);

  /**
   * Documents correspondant à la recherche,
   * toujours triés alphabétiquement.
   */
  const filteredDocuments = useMemo(() => {
    const matchingDocuments =
      activeDocumentSource
        .map((document) => ({
          ...document,
          itemType: "document",
        }))
        .filter((document) => {
          if (!globalSearchActive) {
            return true;
          }

          return (document.name || "")
            .toLowerCase()
            .includes(
              normalizedDocumentSearch
            );
        });

    return sortRows(matchingDocuments, {
      sortBy,
      sortOrder,
      accessors: {
        name: (item) =>
          item.name || "",
        extension: (item) =>
          item.extension || "",
        size: (item) => {
          if (
            item.size === null ||
            item.size === undefined
          ) {
            return null;
          }

          const value = Number(item.size);

          return Number.isNaN(value)
            ? null
            : value;
        },
        createdAt: (item) =>
          item.createdAt
            ? new Date(item.createdAt).getTime()
            : null,
      },
    });
  }, [
    activeDocumentSource,
    globalSearchActive,
    normalizedDocumentSearch,
    sortBy,
    sortOrder,
  ]);

  /**
   * Le nombre de pages dépend uniquement des documents.
   */
  const totalPages = Math.max(
    1,
    Math.ceil(
      filteredDocuments.length / PAGE_SIZE
    )
  );

  /**
   * Limite stricte de 10 documents par page.
   */
  const paginatedDocuments = useMemo(() => {
    const safePage = Math.min(
      page,
      totalPages
    );

    const start =
      (safePage - 1) * PAGE_SIZE;

    return filteredDocuments.slice(
      start,
      start + PAGE_SIZE
    );
  }, [
    filteredDocuments,
    page,
    totalPages,
  ]);

  /**
   * Les sous-dossiers sont affichés en premier,
   * suivis des 10 documents de la page courante.
   */
  const paginatedItems = useMemo(
    () => [
      ...filteredFolderItems,
      ...paginatedDocuments,
    ],
    [
      filteredFolderItems,
      paginatedDocuments,
    ]
  );

  /**
   * Compteur complet utilisé dans l'en-tête.
   */
  const tableItems = useMemo(
    () => [
      ...filteredFolderItems,
      ...filteredDocuments,
    ],
    [
      filteredFolderItems,
      filteredDocuments,
    ]
  );

  const selectedDocs = useMemo(
    () => [...selectedDocIds].map((id) => selectedDocumentCache.get(id)).filter(Boolean),
    [selectedDocIds, selectedDocumentCache]
  );

  const selectedFolders = useMemo(
    () => folders.filter((folder) => selectedFolderIds.has(folder.id)),
    [folders, selectedFolderIds]
  );

  const selectedFolderRoots = useMemo(() => {
    const byId = new Map(folders.map((folder) => [folder.id, folder]));

    return selectedFolders.filter((folder) => {
      let parentId = folder.parentId || null;

      while (parentId) {
        if (selectedFolderIds.has(parentId)) {
          return false;
        }
        parentId = byId.get(parentId)?.parentId || null;
      }

      return true;
    });
  }, [folders, selectedFolders, selectedFolderIds]);

  const selectedCanShare =
    selectedDocs.length > 0 &&
    selectedDocs.every(
      (document) =>
        document.permissions?.canShare
    );

  const selectedCanEdit =
    selectedDocs.length > 0 &&
    selectedDocs.every(
      (document) =>
        document.permissions?.canEdit
    );

  const selectedCanDelete =
    selectedDocs.length > 0 &&
    selectedDocs.every(
      (document) =>
        document.permissions?.canDelete
    );

  const selectedCanManageAccess =
    selectedDocs.length > 0 &&
    selectedDocs.every(
      (document) =>
        document.permissions?.canManageAccess
    );

  const selectedFoldersCanShare =
    selectedFolderRoots.length > 0 &&
    selectedFolderRoots.every(
      (folder) => folder.permissions?.canShare
    );

  const selectedFoldersCanManageAccess =
    selectedFolderRoots.length > 0 &&
    selectedFolderRoots.every((folder) => folder.permissions?.canManageAccess);

  const selectedFoldersCanEdit =
    selectedFolderRoots.length > 0 &&
    selectedFolderRoots.every(
      (folder) => folder.permissions?.canEdit
    );

  const selectedFoldersCanDelete =
    selectedFolderRoots.length > 0 &&
    selectedFolderRoots.every(
      (folder) => folder.permissions?.canDelete
    );

  const editableFolders = useMemo(
    () =>
      folders.filter(
        (folder) =>
          folder.permissions?.canEdit
      ),
    [folders]
  );

  const pageFolderSelectionIds = useMemo(() => {
    const ids = new Set();

    filteredFolderItems.forEach((folder) => {
      collectFolderSubtreeIds(
        folders,
        folder.id
      ).forEach((id) => ids.add(id));
    });

    return ids;
  }, [filteredFolderItems, folders]);

  const allPageDocumentsSelected =
    paginatedDocuments.length === 0 ||
    paginatedDocuments.every(
      (doc) => selectedDocIds.has(doc.id)
    );

  const allPageFoldersSelected =
    pageFolderSelectionIds.size === 0 ||
    [...pageFolderSelectionIds].every(
      (id) => selectedFolderIds.has(id)
    );

  const hasPageSelectableItems =
    paginatedDocuments.length > 0 ||
    pageFolderSelectionIds.size > 0;

  const allPageSelected =
    hasPageSelectableItems &&
    allPageDocumentsSelected &&
    allPageFoldersSelected;

  const createFolder = async () => {
    if (!newFolderName.trim()) return;

    try {
      await foldersApi.create({ name: newFolderName.trim(), parent_id: currentFolderId });
      toast.success("Dossier créé");
      setNewFolderName("");
      setNewFolderOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };


  const onUpload = async (files) => {
    if (!files?.length) return;

    for (const file of Array.from(files)) {
      try {
        await documentsApi.upload(file, currentFolderId);
        toast.success(`${file.name} ajouté`);
      } catch (e) {
        if (e.response?.status === 413) {
          toast.error(
            `${file.name}: fichier trop volumineux. Taille maximale autorisée : 250 Mo.`
          );
        } else {
          toast.error(
            `${file.name}: ${formatApiError(e.response?.data?.detail)}`
          );
        }
      }
    }

    load();
  };

  const deleteFolder = async (folder) => {
    if (!folder || deleting) return;

    setDeleting(true);
    try {
      await foldersApi.remove(folder.id);
      toast.success("Dossier supprimé");

      if (currentFolderId === folder.id) {
        setCurrentFolderId(null);
      } else {
        await load();
      }
      setDeleteTarget(null);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setDeleting(false);
    }
  };

  const deleteDoc = async (doc) => {
    if (!doc || deleting) return;

    setDeleting(true);
    try {
      await documentsApi.remove(doc.id);
      toast.success("Document supprimé");
      await load();
      setDeleteTarget(null);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setDeleting(false);
    }
  };

  const deleteSelectedFolders = async () => {
    if (selectedFolderRoots.length === 0 || deleting) return;

    setDeleting(true);
    const failures = [];
    let deletedCount = 0;

    try {
      for (const folder of selectedFolderRoots) {
        try {
          await foldersApi.remove(folder.id);
          deletedCount += 1;
        } catch (error) {
          failures.push({
            folder,
            message:
              formatApiError(error.response?.data?.detail) ||
              error.message,
          });
        }
      }

      if (deletedCount > 0) {
        toast.success(`${deletedCount} dossier(s) supprimé(s)`);
      }

      if (failures.length > 0) {
        toast.error(
          failures.length === 1
            ? `${failures[0].folder.name}: ${failures[0].message}`
            : `${failures.length} dossier(s) n'ont pas pu être supprimés`
        );
      }

      setSelectedFolderIds(new Set());
      setDeleteTarget(null);

      if (currentFolderId) {
        setCurrentFolderId(null);
      } else {
        await load();
      }
    } finally {
      setDeleting(false);
    }
  };

  /**
   * Supprime tous les documents sélectionnés en une seule
   * opération backend sécurisée et auditée.
   */
  const deleteSelectedDocs = async () => {
    if (selectedDocs.length === 0 || deleting) return;

    setDeleting(true);
    try {
      const result = await documentsApi.removeMany(
        selectedDocs.map((document) => document.id)
      );

      const deletedCount = Number(
        result?.deletedCount || 0
      );

      toast.success(
        `${deletedCount} document(s) supprimé(s)`
      );

      setSelectedDocIds(new Set());
      await load();
      setDeleteTarget(null);
    } catch (error) {
      toast.error(
        formatApiError(
          error.response?.data?.detail
        ) || error.message
      );
    } finally {
      setDeleting(false);
    }
  };

  const launchDocumentDownloads = (
    docsToDownload
  ) => {
    if (!docsToDownload?.length) {
      toast.info("Aucun document à télécharger");
      return;
    }

    docsToDownload.forEach((doc, index) => {
      setTimeout(() => {
        const link =
          document.createElement("a");

        link.href = fileUrl(doc.id);
        link.download = doc.name;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }, index * 200);
    });

    toast.success(
      `${docsToDownload.length} téléchargement(s) lancé(s)`
    );
  };

  const downloadSelectedDocs = () => {
    launchDocumentDownloads(selectedDocs);
  };

  const downloadFolders = async (
    foldersToDownload
  ) => {
    if (!foldersToDownload?.length) {
      return;
    }

    try {
      const folderIds = new Set();

      foldersToDownload.forEach(
        (folder) => {
          collectFolderSubtreeIds(
            folders,
            folder.id
          ).forEach(
            (id) =>
              folderIds.add(id)
          );
        }
      );

      const allDocuments =
        await documentsApi.list();

      const docsToDownload =
        allDocuments.filter(
          (doc) =>
            folderIds.has(
              doc.folderId
            )
        );

      launchDocumentDownloads(
        docsToDownload
      );
    } catch (error) {
      toast.error(
        formatApiError(
          error.response?.data?.detail
        ) || error.message
      );
    }
  };

  const downloadSelectedFolders = () => {
    void downloadFolders(
      selectedFolderRoots
    );
  };

  const openShare = (item) => {
    setShareItem(item);
    setShareOpen(true);
  };

  const openBulkShare = () => {
    if (selectedDocs.length === 0) return;

    openShare({
      type: "selection",
      id: "bulk-selection",
      name: `${selectedDocs.length} documents sélectionnés`,
      items: selectedDocs.map((doc) => ({
        type: "document",
        id: doc.id,
      })),
    });
  };

  const openBulkFolderShare = () => {
    if (selectedFolderRoots.length === 0) {
      return;
    }

    openShare({
      type: "selection",
      id: "bulk-folder-selection",
      name:
        `${selectedFolderRoots.length} dossier(s) sélectionné(s)`,
      items: selectedFolderRoots.map(
        (folder) => ({
          type: "folder",
          id: folder.id,
        })
      ),
    });
  };

  const openAccess = (type, items) => {
    if (!items?.length || items.some((item) => !item.permissions?.canManageAccess)) {
      toast.error("Vous ne pouvez pas gérer les accès de cette sélection.");
      return;
    }
    setAccessSelection({ type, items });
  };

  const renameItem = async (target, name) => {
    const trimmedName = name.trim();

    if (!trimmedName || trimmedName === target.item.name) {
      setRenameTarget(null);
      return;
    }

    try {
      const updated =
        target.type === "folder"
          ? await foldersApi.update(target.item.id, { name: trimmedName })
          : await documentsApi.update(target.item.id, { name: trimmedName });

      if (target.type === "folder") {
        setFolders((items) =>
          items.map((folder) =>
            folder.id === updated.id ? { ...folder, ...updated } : folder
          )
        );
      } else {
        setDocuments((items) =>
          items.map((doc) =>
            doc.id === updated.id ? { ...doc, ...updated } : doc
          )
        );
      }

      setGlobalSearchLoaded(false);
      setRenameTarget(null);

      toast.success(
        target.type === "folder" ? "Dossier renommé" : "Document renommé"
      );
    } catch (error) {
      toast.error(
        formatApiError(error.response?.data?.detail) || error.message
      );
    }
  };

  const moveDocuments = async (docsToMove, targetFolderId) => {
    if (!docsToMove.length) return;

    try {
      await Promise.all(
        docsToMove.map((doc) => documentsApi.update(doc.id, { folder_id: targetFolderId }))
      );

      toast.success(docsToMove.length > 1 ? "Documents déplacés" : "Document déplacé");
      setMoveItem(null);
      setSelectedDocIds(new Set());
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const moveFolders = async (foldersToMove, targetFolderId) => {
    if (!foldersToMove?.length) return;

    const failures = [];
    let movedCount = 0;

    for (const folderToMove of foldersToMove) {
      try {
        await foldersApi.update(folderToMove.id, {
          parent_id: targetFolderId,
        });
        movedCount += 1;
      } catch (error) {
        failures.push({
          folder: folderToMove,
          message:
            formatApiError(error.response?.data?.detail) ||
            error.message,
        });
      }
    }

    if (movedCount > 0) {
      toast.success(
        movedCount === 1
          ? "Dossier déplacé"
          : `${movedCount} dossiers déplacés`
      );
    }

    if (failures.length > 0) {
      toast.error(
        failures.length === 1
          ? `${failures[0].folder.name}: ${failures[0].message}`
          : `${failures.length} dossier(s) n'ont pas pu être déplacés`
      );
    }

    setMoveItem(null);
    setSelectedFolderIds(new Set());
    await load();
  };

  const toggleExpandedFolder = (folderId) => {
    setExpandedFolderIds((current) => {
      const folderById = new Map(
        folders.map((folder) => [
          folder.id,
          folder,
        ])
      );

      if (current.has(folderId)) {
        const closedSubtreeIds =
          collectFolderSubtreeIds(
            folders,
            folderId
          );

        return new Set(
          [...current].filter(
            (id) =>
              !closedSubtreeIds.has(id)
          )
        );
      }

      const openPath = new Set([folderId]);
      let parentId =
        folderById.get(folderId)?.parentId ||
        null;

      while (parentId) {
        openPath.add(parentId);
        parentId =
          folderById.get(parentId)?.parentId ||
          null;
      }

      return openPath;
    });
  };

  const toggleFolderSelection = (folderId) => {
    const subtreeIds =
      collectFolderSubtreeIds(
        folders,
        folderId
      );

    setSelectedFolderIds((current) => {
      const next = new Set(current);
      const shouldDeselect =
        current.has(folderId);

      subtreeIds.forEach((id) => {
        if (shouldDeselect) {
          next.delete(id);
        } else {
          next.add(id);
        }
      });

      return next;
    });
  };

  const toggleDocSelection = (docId) => {
    const doc = activeDocumentSource.find((item) => item.id === docId);
    if (doc) {
      setSelectedDocumentCache((current) => new Map(current).set(docId, doc));
    }
    setSelectedDocIds((current) => {
      const next = new Set(current);

      if (next.has(docId)) {
        next.delete(docId);
      } else {
        next.add(docId);
      }

      return next;
    });
  };

  const togglePageSelection = () => {
    setSelectedDocumentCache((current) => {
      const next = new Map(current);
      paginatedDocuments.forEach((doc) => next.set(doc.id, doc));
      return next;
    });
    setSelectedDocIds((current) => {
      const next = new Set(current);

      paginatedDocuments.forEach((doc) => {
        if (allPageSelected) {
          next.delete(doc.id);
        } else {
          next.add(doc.id);
        }
      });

      return next;
    });

    setSelectedFolderIds((current) => {
      const next = new Set(current);

      pageFolderSelectionIds.forEach((id) => {
        if (allPageSelected) {
          next.delete(id);
        } else {
          next.add(id);
        }
      });

      return next;
    });
  };


  const renderDocumentActions = ({
    includeMobileFolders = false,
  } = {}) => (
    <>
      {includeMobileFolders && (
        <button
          type="button"
          onClick={() => setMobileFoldersOpen(true)}
          className="documents-v2-secondary-button documents-v2-mobile-folder-button"
          data-testid="mobile-folders-open"
        >
          <List size={17} weight="bold" />
          Dossiers
        </button>
      )}

      {canAddContent && (
        <>
          <button
            type="button"
            onClick={() => setNewFolderOpen(true)}
            data-testid="new-folder-btn"
            className="documents-v2-secondary-button"
          >
            <FolderPlus size={18} weight="duotone" />
            Nouveau dossier
          </button>

          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            data-testid="upload-btn"
            className="documents-v2-primary-button"
          >
            <UploadSimple size={18} weight="bold" />
            Importer un fichier
          </button>
        </>
      )}
    </>
  );

  const renderDocumentSearch = (placement) => (
    <div
      className={`documents-v2-header-search documents-v2-header-search-${placement}`}
      data-testid={`documents-search-${placement}`}
    >
      <MagnifyingGlass
        size={17}
        aria-hidden="true"
      />

      <input
        value={documentSearch}
        onChange={(event) =>
          setDocumentSearch(
            event.target.value
          )
        }
        placeholder="Rechercher un dossier ou un document"
        aria-label="Rechercher un dossier ou un document"
        data-testid="document-search"
      />
    </div>
  );


  const renderFolderNavigation = ({
    mobile = false,
  } = {}) => (
    <>
      {mobile && (
        <div className="documents-v2-mobile-panel-header justify-end">
          <button
            type="button"
            onClick={() => setMobileFoldersOpen(false)}
            className="documents-v2-icon-button"
            aria-label="Fermer la navigation"
            data-testid="mobile-folders-close"
          >
            <X size={18} />
          </button>
        </div>
      )}

      <div className={`documents-v2-folder-tree-scroll max-h-[64vh] overflow-y-auto pr-1 ${
        mobile ? "space-y-1" : "acta-context-sidebar-list"
      }`}>
        <button
          type="button"
          onClick={() => setCurrentFolderId(null)}
          className={
            mobile
              ? `w-full flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm text-left ${
                  !currentFolderId
                    ? "bg-[var(--do-accent-soft)] text-[var(--do-accent)]"
                    : "text-[var(--do-ink)]"
                }`
              : `acta-desktop-context-link ${!currentFolderId ? "is-active" : ""}`
          }
          data-testid="folder-tree-root"
        >
          <FolderOpen size={18} weight="duotone" />
          <span>Tous les documents</span>
          <span aria-hidden="true" />
        </button>

        {folderTree.map((folder) => (
          <FolderTreeNode
            key={folder.id}
            folder={folder}
            level={1}
            currentFolderId={currentFolderId}
            expandedFolderIds={expandedFolderIds}
            onToggle={toggleExpandedFolder}
            onSelect={setCurrentFolderId}
            mobile={mobile}
          />
        ))}
      </div>
    </>
  );

  return (
    <div
      className="documents-v2-page acta-standard-page"
      data-testid="documents-page"
    >
      <AlertDialog
        open={Boolean(deleteTarget)}
        onOpenChange={(open) => {
          if (!open && !deleting) setDeleteTarget(null);
        }}
      >
        <AlertDialogContent data-testid="documents-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {deleteTarget?.type === "folder"
                ? "Supprimer ce dossier ?"
                : deleteTarget?.type === "bulkFolders"
                  ? "Supprimer les dossiers sélectionnés ?"
                  : deleteTarget?.type === "bulk"
                    ? "Supprimer les documents sélectionnés ?"
                    : "Supprimer ce document ?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget?.type === "folder"
                ? `Le dossier « ${deleteTarget?.item?.name || ""} » et tout son contenu seront supprimés.`
                : deleteTarget?.type === "bulkFolders"
                  ? `${deleteTarget?.count || 0} dossier(s) sélectionné(s) et leur contenu seront supprimés.`
                  : deleteTarget?.type === "bulk"
                    ? `${deleteTarget?.count || 0} document(s) seront supprimés définitivement.`
                    : `Le document « ${deleteTarget?.item?.name || ""} » sera supprimé.`}
            </AlertDialogDescription>
          </AlertDialogHeader>

          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              disabled={deleting}
              onClick={(event) => {
                event.preventDefault();
                const target = deleteTarget;
                if (!target) return;

                if (target.type === "folder") {
                  deleteFolder(target.item);
                } else if (target.type === "bulkFolders") {
                  deleteSelectedFolders();
                } else if (target.type === "bulk") {
                  deleteSelectedDocs();
                } else {
                  deleteDoc(target.item);
                }
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? "Suppression…" : "Supprimer"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {!isDesktopViewport && (
        <header className="documents-v2-header">
          <div className="documents-v2-header-actions">
            {renderDocumentSearch("toolbar")}

            {renderDocumentActions({
              includeMobileFolders: true,
            })}
          </div>
        </header>
      )}

      <input
        ref={fileInputRef}
        type="file"
        multiple
        hidden
        accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.png,.jpg,.jpeg,.webp,.html,.htm"
        onClick={(event) => {
          event.currentTarget.value = "";
        }}
        onChange={(event) => {
          const files =
            Array.from(
              event.target.files || []
            );

          event.target.value = "";
          onUpload(files);
        }}
        data-testid="upload-input"
      />

      {newFolderOpen && (
        <div
          className="documents-v2-new-folder-form"
          data-testid="new-folder-form"
        >
          <input
            autoFocus
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && createFolder()}
            placeholder="Nom du dossier"
            data-testid="new-folder-input"
            className="documents-v2-input flex-1"
          />

          <button
            onClick={createFolder}
            data-testid="create-folder-confirm"
            className="documents-v2-primary-button"
          >
            Créer
          </button>

          <button
            onClick={() => {
              setNewFolderOpen(false);
              setNewFolderName("");
            }}
            className="documents-v2-text-button"
          >
            Annuler
          </button>
        </div>
      )}

      {desktopTreeHost &&
        isDesktopViewport &&
        createPortal(
          <div
            className="documents-v2-desktop-folder-tree"
            data-testid="folders-tree-panel"
          >
            <div
              className="documents-v2-desktop-tree-search-wrap"
              data-testid="documents-desktop-tree-search"
            >
              {renderDocumentSearch("tree")}
            </div>

            {renderFolderNavigation()}
          </div>,
          desktopTreeHost
        )}

      {loading ? (
        <div className="label-eyebrow lirah-pulse">Chargement</div>
      ) : (
        <div className="documents-v2-layout">
          {mobileFoldersOpen && (
            <button
              type="button"
              className="documents-v2-mobile-backdrop"
              onClick={() => setMobileFoldersOpen(false)}
              aria-label="Fermer le panneau dossiers"
            />
          )}

          {!isDesktopViewport && (
            <aside
              className={`documents-v2-folders-panel ${
                mobileFoldersOpen
                  ? "documents-v2-folders-panel-open"
                  : ""
              }`}
              data-testid="folders-tree-panel"
            >
              {renderFolderNavigation({
                mobile: true,
              })}
            </aside>
          )}

          <section className="min-w-0">
            <div className="documents-v2-content-card" data-testid="documents-table">
              <div className="documents-v2-content-header">
                <div className="documents-v2-breadcrumb">
                  <FolderOpen
                    size={17}
                    weight="duotone"
                  />

                  <button
                    type="button"
                    onClick={() =>
                      setCurrentFolderId(null)
                    }
                  >
                    Tous les documents
                  </button>

                  {breadcrumb.map((folder) => (
                    <React.Fragment
                      key={folder.id}
                    >
                      <CaretRight size={14} />

                      <button
                        type="button"
                        onClick={() =>
                          setCurrentFolderId(
                            folder.id
                          )
                        }
                      >
                        {folder.name}
                      </button>
                    </React.Fragment>
                  ))}
                </div>

                {isDesktopViewport && (
                  <div
                    className="documents-v2-content-actions"
                    data-testid="documents-desktop-content-actions"
                  >
                    {renderDocumentActions()}
                  </div>
                )}
              </div>

              {(selectedDocIds.size > 0 || selectedFolderIds.size > 0) && (
                <div className="documents-v2-bulk-bar" data-testid="documents-global-selection">
                  <div className="documents-v2-bulk-count">
                    <CheckSquare size={18} weight="fill" />
                    {selectedDocIds.size + selectedFolderIds.size} élément(s) sélectionné(s)
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {selectedDocs.map((doc) => (
                      <button key={`doc-${doc.id}`} type="button" className="documents-v2-bulk-button" onClick={() => toggleDocSelection(doc.id)} aria-label={`Retirer ${doc.name} de la sélection`}>{doc.name} <X size={14} /></button>
                    ))}
                    {selectedFolderRoots.map((folder) => (
                      <button key={`folder-${folder.id}`} type="button" className="documents-v2-bulk-button" onClick={() => toggleFolderSelection(folder.id)} aria-label={`Retirer ${folder.name} de la sélection`}>{folder.name} <X size={14} /></button>
                    ))}
                    <button type="button" className="documents-v2-bulk-button" data-testid="documents-clear-all-selection" onClick={() => { setSelectedDocIds(new Set()); setSelectedFolderIds(new Set()); setSelectedDocumentCache(new Map()); }}>Tout désélectionner</button>
                  </div>
                </div>
              )}

              {selectedFolders.length > 0 && (
                <div
                  className="documents-v2-bulk-bar documents-v2-bulk-bar-nowrap"
                  data-testid="bulk-folder-actions-bar"
                >
                  <div className="documents-v2-bulk-count">
                    <CheckSquare size={18} weight="fill" />
                    {selectedFolders.length} dossier(s) sélectionné(s)
                  </div>

                  <div className="documents-v2-bulk-actions-nowrap">
                    {selectedFoldersCanShare && (
                      <button
                        type="button"
                        onClick={openBulkFolderShare}
                        className="documents-v2-bulk-button"
                        data-testid="bulk-folder-share"
                      >
                        <ShareNetwork size={14} />
                        Partager
                      </button>
                    )}

                    {selectedFoldersCanEdit && (
                      <button
                        type="button"
                        onClick={() =>
                          setMoveItem({
                            type: "folders",
                            folders: selectedFolderRoots,
                          })
                        }
                        className="documents-v2-bulk-button"
                        data-testid="bulk-folder-move"
                      >
                        <FolderIcon size={14} />
                        Déplacer
                      </button>
                    )}

                    <button
                      type="button"
                      onClick={downloadSelectedFolders}
                      className="documents-v2-bulk-button"
                      data-testid="bulk-folder-download"
                    >
                      <DownloadSimple size={14} />
                      Télécharger
                    </button>

                    {selectedFoldersCanManageAccess && (
                      <button
                        type="button"
                        onClick={() =>
                          openAccess("folder", selectedFolderRoots)
                        }
                        className="documents-v2-bulk-button"
                        data-testid="bulk-folder-access"
                      >
                        <Eye size={14} />
                        Gérer les accès
                      </button>
                    )}

                    <button
                      type="button"
                      onClick={() =>
                        setSelectedFolderIds(new Set())
                      }
                      className="documents-v2-bulk-button"
                      data-testid="bulk-folder-clear-selection"
                    >
                      <X size={14} />
                      Désélectionner
                    </button>

                    {selectedFoldersCanDelete && (
                      <button
                        type="button"
                        onClick={() =>
                          setDeleteTarget({
                            type: "bulkFolders",
                            count: selectedFolderRoots.length,
                          })
                        }
                        className="documents-v2-bulk-button documents-v2-danger-button"
                        data-testid="bulk-folder-delete"
                      >
                        <Trash size={14} />
                        Supprimer
                      </button>
                    )}
                  </div>
                </div>
              )}

              {selectedDocs.length > 0 && (
                <div
                  className="documents-v2-bulk-bar"
                  data-testid="bulk-actions-bar"
                >
                  <div className="documents-v2-bulk-count">
                    <CheckSquare size={18} weight="fill" />
                    {selectedDocs.length} document(s) sélectionné(s)
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    {selectedCanShare && (
                      <button
                        onClick={openBulkShare}
                        className="documents-v2-bulk-button"
                        data-testid="bulk-share"
                      >
                        <ShareNetwork size={14} />
                        Partager
                      </button>
                    )}

                    {selectedCanEdit && (
                      <button
                        onClick={() =>
                          setMoveItem({
                            type: "bulk",
                            docs: selectedDocs,
                          })
                        }
                        className="documents-v2-bulk-button"
                        data-testid="bulk-move"
                      >
                        <FolderIcon size={14} />
                        Déplacer
                      </button>
                    )}

                    <button
                      onClick={downloadSelectedDocs}
                      className="documents-v2-bulk-button"
                      data-testid="bulk-download"
                    >
                      <DownloadSimple size={14} />
                      Télécharger
                    </button>

                    {selectedCanManageAccess && (
                      <button
                        onClick={() => openAccess("document", selectedDocs)}
                        className="documents-v2-bulk-button"
                        data-testid="bulk-document-access"
                      >
                        <Eye size={14} />
                        Gérer les accès
                      </button>
                    )}

                    <button
                      type="button"
                      onClick={() => setSelectedDocIds(new Set())}
                      className="documents-v2-bulk-button"
                      data-testid="bulk-clear-selection"
                    >
                      <X size={14} />
                      Désélectionner
                    </button>

                    {selectedCanDelete && (
                      <button
                        type="button"
                        onClick={() => setDeleteTarget({ type: "bulk", count: selectedDocs.length })}
                        className="documents-v2-bulk-button documents-v2-danger-button"
                        data-testid="bulk-delete"
                      >
                        <Trash size={14} />
                        Supprimer
                      </button>
                    )}
                  </div>
                </div>
              )}

              {tableItems.length === 0 ? (
                <div className="p-12 text-center border-dashed">
                  <FolderOpen size={42} weight="duotone" className="text-[var(--do-muted)] mx-auto mb-3" />
                  <div className="font-heading font-medium text-sm">Aucun document ici</div>
                  <div className="text-xs text-[var(--do-muted)] mt-1">
                    Uploadez votre premier fichier ou changez de dossier.
                  </div>
                </div>
              ) : (
                <>
                  <DocumentsMobileList
                    items={paginatedItems}
                    selectedDocIds={selectedDocIds}
                    selectedFolderIds={selectedFolderIds}
                    onToggleDocument={toggleDocSelection}
                    onToggleFolder={toggleFolderSelection}
                    onOpenFolder={setCurrentFolderId}
                    onPreviewDocument={setPreview}
                    onOpenActions={(item) =>
                      setMobileActionSheet({
                        type: item.itemType,
                        menuId:
                          `mobile-${item.itemType}-${item.id}`,
                        item,
                      })
                    }
                  />

                  <div className="documents-v2-table-wrap">
                    <table className="documents-v2-table">
                      <thead>
                        <tr className="text-left text-[11px] uppercase tracking-[0.15em] text-[var(--do-muted)]">
                          <th className="px-4 py-2 font-semibold w-10">
                            <input
                              type="checkbox"
                              checked={allPageSelected}
                              onChange={togglePageSelection}
                              className="h-4 w-4 accent-[var(--do-accent)]"
                              aria-label="Sélectionner les éléments affichés et leurs sous-dossiers"
                              data-testid="select-page-documents"
                            />
                          </th>
                          <SortableTableHeader label="Nom" field="name" sortBy={sortBy} sortOrder={sortOrder} onSort={handleDocumentSort} className="px-4 py-2 font-semibold" testId="documents-sort-name" />
                          <SortableTableHeader label="Type" field="extension" sortBy={sortBy} sortOrder={sortOrder} onSort={handleDocumentSort} className="px-4 py-2 font-semibold w-24" testId="documents-sort-extension" />
                          <SortableTableHeader label="Taille" field="size" sortBy={sortBy} sortOrder={sortOrder} onSort={handleDocumentSort} className="px-4 py-2 font-semibold w-28" testId="documents-sort-size" />
                          <SortableTableHeader label="Modifié le" field="createdAt" sortBy={sortBy} sortOrder={sortOrder} onSort={handleDocumentSort} className="px-4 py-2 font-semibold w-40" testId="documents-sort-createdAt" />
                          <th className="px-4 py-2 font-semibold w-48 text-right">Actions</th>
                        </tr>
                      </thead>

                      <tbody>
                        {paginatedItems.map((doc) => {
                          if (doc.itemType === "folder") {
                            const isFolderSelected =
                              selectedFolderIds.has(doc.id);

                            return (
                              <tr
                                key={`folder-${doc.id}`}
                                className={`border-b border-[var(--do-border)] hover:bg-[var(--do-bg-solid)] ${
                                  isFolderSelected
                                    ? "bg-[var(--do-accent-soft)]"
                                    : ""
                                }`}
                                data-testid={`folder-row-${doc.id}`}
                              >
                                <td className="px-4 py-2.5">
                                  <input
                                    type="checkbox"
                                    checked={isFolderSelected}
                                    onChange={() =>
                                      toggleFolderSelection(doc.id)
                                    }
                                    className="h-4 w-4 accent-[var(--do-accent)]"
                                    data-testid={`select-folder-${doc.id}`}
                                  />
                                </td>

                                <td className="px-4 py-2.5">
                                  <button
                                    onClick={() => setCurrentFolderId(doc.id)}
                                    className="documents-v2-item-name-trigger flex items-center gap-3 text-left min-w-0"
                                    data-testid={`open-folder-row-${doc.id}`}
                                  >
                                    <FolderIcon size={21} weight="duotone" className="text-[var(--lirah-primary)] shrink-0" />
                                    <span
                                      className="documents-v2-item-name min-w-0 flex-1 font-medium text-[var(--do-ink)] truncate max-w-md"
                                      title={doc.name}
                                    >
                                      {doc.name}
                                    </span>
                                  </button>
                                </td>

                                <td className="px-4 py-2.5 text-[var(--do-muted)] text-[11px] font-mono-stat">
                                  DOSSIER
                                </td>

                                <td className="px-4 py-2.5 text-[var(--do-muted)] font-mono-stat text-xs">—</td>

                                <td className="px-4 py-2.5 text-[var(--do-muted)] font-mono-stat text-xs">
                                  {doc.createdAt ? new Date(doc.createdAt).toLocaleDateString("fr-FR", { dateStyle: "medium" }) : "—"}
                                </td>

                                <td className="px-4 py-2.5 text-right">
                                  <div
                                    className="documents-v2-action-menu"
                                    data-documents-action-menu
                                  >
                                    <button
                                      type="button"
                                      className="acta-action-trigger documents-v2-action-trigger"
                                      aria-expanded={
                                        actionMenuId === `folder-${doc.id}`
                                      }
                                      onClick={(event) =>
                                        openRowActionMenu(event, {
                                          type: "folder",
                                          menuId: `folder-${doc.id}`,
                                          item: doc,
                                        })
                                      }
                                      data-testid={`actions-folder-row-${doc.id}`}
                                    >
                                      <DotsThreeVertical size={20} weight="bold" />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          }

                          const Icon = iconFor(doc.extension);
                          const accessCount = (doc.userAccessLevels || []).length
                            || (doc.visibleToUserIds || []).length + (doc.visibleToRoles || []).length;
                          const restricted = accessCount > 0 || (doc.visibleToRoles || []).length > 0;
                          const isSelected = selectedDocIds.has(doc.id);

                          return (
                            <tr
                              key={doc.id}
                              className={`border-b border-[var(--do-border)] hover:bg-[var(--do-bg-solid)] ${
                                isSelected ? "bg-[var(--do-accent-soft)]" : ""
                              }`}
                              data-testid={`document-row-${doc.id}`}
                            >
                              <td className="px-4 py-2.5">
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => toggleDocSelection(doc.id)}
                                  className="h-4 w-4 accent-[var(--do-accent)]"
                                  data-testid={`select-doc-${doc.id}`}
                                />
                              </td>

                              <td className="px-4 py-2.5">
                                <div className="flex min-w-0 items-center gap-2">
                                  <button
                                    onClick={() => setPreview(doc)}
                                    className="documents-v2-item-name-trigger flex min-w-0 flex-1 items-center gap-3 text-left"
                                  >
                                    <Icon size={20} weight="duotone" className="text-[var(--lirah-primary)] shrink-0" />
                                    <span
                                      className="documents-v2-item-name min-w-0 flex-1 font-medium text-[var(--do-ink)] truncate max-w-md"
                                      title={doc.name}
                                    >
                                      {doc.name}
                                    </span>
                                    {restricted && (
                                      <span
                                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] uppercase tracking-wider shrink-0"
                                        style={{
                                          background: "rgba(251, 191, 36, 0.14)",
                                          color: "var(--do-warm-deep, #F59E0B)",
                                          border: "1px solid rgba(251, 191, 36, 0.3)",
                                        }}
                                        data-testid={`doc-access-badge-${doc.id}`}
                                      >
                                        <Eye size={9} weight="fill" />
                                        <span>Accès · {accessCount}</span>
                                      </span>
                                    )}
                                  </button>

                                  <div className="flex shrink-0 items-center gap-1 md:hidden">
                                    {doc.permissions?.canManageAccess && (
                                      <button
                                        type="button"
                                        onClick={() =>
                                          openAccess("document", [doc])
                                        }
                                        className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-[var(--do-border)] text-[var(--do-muted)]"
                                        aria-label={`Gérer les accès de ${doc.name}`}
                                        data-testid={`mobile-inline-access-doc-${doc.id}`}
                                      >
                                        <Eye size={16} />
                                      </button>
                                    )}

                                    {doc.permissions?.canDelete && (
                                      <button
                                        type="button"
                                        onClick={() =>
                                          setDeleteTarget({
                                            type: "document",
                                            item: doc,
                                          })
                                        }
                                        className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-[var(--do-border)] text-[var(--do-accent)]"
                                        aria-label={`Supprimer ${doc.name}`}
                                        data-testid={`mobile-inline-delete-doc-${doc.id}`}
                                      >
                                        <Trash size={16} />
                                      </button>
                                    )}
                                  </div>
                                </div>
                              </td>

                              <td className="px-4 py-2.5 text-[var(--do-muted)] uppercase text-[11px] font-mono-stat">
                                {doc.extension}
                              </td>

                              <td className="px-4 py-2.5 text-[var(--do-muted)] font-mono-stat text-xs">
                                {humanSize(doc.size)}
                              </td>

                              <td className="px-4 py-2.5 text-[var(--do-muted)] font-mono-stat text-xs">
                                {new Date(doc.createdAt).toLocaleDateString("fr-FR", { dateStyle: "medium" })}
                              </td>

                              <td className="px-4 py-2.5 text-right">
                                <div
                                  className="documents-v2-action-menu"
                                  data-documents-action-menu
                                >
                                  <button
                                    type="button"
                                    className="acta-action-trigger documents-v2-action-trigger"
                                    aria-expanded={
                                      actionMenuId === `document-${doc.id}`
                                    }
                                    onClick={(event) =>
                                      openRowActionMenu(event, {
                                        type: "document",
                                        menuId: `document-${doc.id}`,
                                        item: doc,
                                      })
                                    }
                                    data-testid={`actions-doc-${doc.id}`}
                                  >
                                    <DotsThreeVertical size={20} weight="bold" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  <div className="documents-v2-pagination">
                    <div className="documents-v2-pagination-summary text-xs text-[var(--do-muted)] font-mono-stat">
                      10 documents par page · {filteredDocuments.length} document(s) · {filteredFolderItems.length} dossier(s)
                    </div>

                    <div className="documents-v2-pagination-desktop-controls flex items-center gap-3">
                      <button
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        disabled={page <= 1}
                        className="documents-v2-pagination-button"
                        data-testid="documents-prev-page"
                      >
                        Précédent
                      </button>

                      <div className="text-xs font-mono-stat">
                        Page {Math.min(page, totalPages)} / {totalPages}
                      </div>

                      <button
                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                        disabled={page >= totalPages}
                        className="documents-v2-pagination-button"
                        data-testid="documents-next-page"
                      >
                        Suivant
                      </button>
                    </div>

                    <div
                      className="documents-v2-pagination-mobile-controls"
                      data-testid="documents-mobile-pagination"
                    >
                      <button
                        type="button"
                        onClick={() =>
                          setPage((p) =>
                            Math.max(1, p - 1)
                          )
                        }
                        disabled={page <= 1}
                        aria-label="Page précédente"
                        data-testid="documents-mobile-prev-page"
                      >
                        <CaretLeft
                          size={15}
                          weight="bold"
                        />
                      </button>

                      <span className="documents-v2-pagination-mobile-page">
                        {Math.min(page, totalPages)}
                        {" / "}
                        {totalPages}
                      </span>

                      <button
                        type="button"
                        onClick={() =>
                          setPage((p) =>
                            Math.min(
                              totalPages,
                              p + 1
                            )
                          )
                        }
                        disabled={page >= totalPages}
                        aria-label="Page suivante"
                        data-testid="documents-mobile-next-page"
                      >
                        <CaretRight
                          size={15}
                          weight="bold"
                        />
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </section>
        </div>
      )}


      <ActaActionSheet
        open={Boolean(mobileActionSheet)}
        title="Actions"
        subtitle={mobileActionSheet?.item?.name}
        onClose={() => setMobileActionSheet(null)}
        testId="documents-mobile-action-sheet"
      >
        {mobileActionSheet?.type === "folder" && (
          <ActaActionMenuItem
            icon={<FolderOpen size={18} />}
            label="Ouvrir"
            onClick={() => {
              const currentItem =
                mobileActionSheet.item;

              setMobileActionSheet(null);
              setCurrentFolderId(
                currentItem.id
              );
            }}
            testId={`mobile-open-folder-${mobileActionSheet.item?.id}`}
          />
        )}

        {mobileActionSheet?.type === "document" && (
          <ActaActionMenuItem
            icon={<Eye size={18} />}
            label="Prévisualiser"
            onClick={() => {
              const currentItem = mobileActionSheet.item;
              setMobileActionSheet(null);
              setPreview(currentItem);
            }}
            testId={`mobile-preview-doc-${mobileActionSheet.item?.id}`}
          />
        )}

        {mobileActionSheet?.item && (
          <ActaActionMenuItem
            icon={<DownloadSimple size={18} />}
            label="Télécharger"
            onClick={() => {
              const currentItem =
                mobileActionSheet.item;

              const type =
                mobileActionSheet.type;

              setMobileActionSheet(null);

              if (type === "folder") {
                void downloadFolders([
                  currentItem,
                ]);
                return;
              }

              launchDocumentDownloads([
                currentItem,
              ]);
            }}
            testId={`mobile-download-${mobileActionSheet.type}-${mobileActionSheet.item?.id}`}
          />
        )}

        {mobileActionSheet?.item?.permissions?.canEdit && (
          <ActaActionMenuItem
            icon={<PencilSimple size={18} />}
            label="Renommer"
            onClick={() => {
              const currentItem = mobileActionSheet.item;
              const type = mobileActionSheet.type;
              setMobileActionSheet(null);
              setRenameTarget({ type, item: currentItem });
            }}
            testId={`mobile-rename-${mobileActionSheet.type}-${mobileActionSheet.item?.id}`}
          />
        )}

        {mobileActionSheet?.item?.permissions?.canManageAccess && (
          <ActaActionMenuItem
            icon={<Eye size={18} />}
            label="Gérer les accès"
            onClick={() => {
              const currentItem = mobileActionSheet.item;
              const type = mobileActionSheet.type;

              setMobileActionSheet(null);

              openAccess(type, [currentItem]);
            }}
            testId={`mobile-access-${mobileActionSheet.type}-${mobileActionSheet.item?.id}`}
          />
        )}

        {mobileActionSheet?.item?.permissions?.canShare && (
          <ActaActionMenuItem
            icon={<ShareNetwork size={18} />}
            label="Partager"
            onClick={() => {
              const currentItem = mobileActionSheet.item;
              const type = mobileActionSheet.type;

              setMobileActionSheet(null);

              openShare({
                type,
                id: currentItem.id,
                name: currentItem.name,
              });
            }}
            testId={`mobile-share-${mobileActionSheet.type}-${mobileActionSheet.item?.id}`}
          />
        )}

        {mobileActionSheet?.item?.permissions?.canEdit && (
          <ActaActionMenuItem
            icon={<FolderIcon size={18} />}
            label="Déplacer"
            onClick={() => {
              const currentItem = mobileActionSheet.item;
              const type = mobileActionSheet.type;

              setMobileActionSheet(null);

              if (type === "folder") {
                setMoveItem({
                  type: "folders",
                  folders: [currentItem],
                });
              } else {
                setMoveItem({
                  type: "single",
                  docs: [currentItem],
                });
              }
            }}
            testId={`mobile-move-${mobileActionSheet.type}-${mobileActionSheet.item?.id}`}
          />
        )}

        {mobileActionSheet?.item?.permissions?.canDelete && (
          <ActaActionMenuItem
            icon={<Trash size={18} />}
            label="Supprimer"
            danger
            onClick={() => {
              const currentItem = mobileActionSheet.item;
              const type = mobileActionSheet.type;

              setMobileActionSheet(null);

              if (type === "folder") {
                setDeleteTarget({ type: "folder", item: currentItem });
              } else {
                setDeleteTarget({ type: "document", item: currentItem });
              }
            }}
            testId={`mobile-delete-${mobileActionSheet.type}-${mobileActionSheet.item?.id}`}
          />
        )}
      </ActaActionSheet>

      <ActaActionMenu
        open={Boolean(desktopActionMenu)}
        anchorEl={desktopActionMenu?.anchorEl}
        onClose={closeAllActionMenus}
        width={230}
        estimatedHeight={
          desktopActionMenu
            ? getDesktopMenuHeight(desktopActionMenu)
            : 220
        }
        testId="documents-desktop-action-menu"
      >
        {desktopActionMenu?.type === "document" ? (
          <>
            <ActaActionMenuItem
              icon={<Eye size={16} />}
              label="Prévisualiser"
              onClick={() => {
                const doc = desktopActionMenu.item;
                closeAllActionMenus();
                setPreview(doc);
              }}
            />

            {desktopActionMenu.item.permissions?.canEdit && (
              <ActaActionMenuItem
                icon={<PencilSimple size={16} />}
                label="Renommer"
                onClick={() => {
                  const doc = desktopActionMenu.item;
                  closeAllActionMenus();
                  setRenameTarget({ type: "document", item: doc });
                }}
              />
            )}

            {desktopActionMenu.item.permissions?.canManageAccess && (
              <ActaActionMenuItem
                icon={<Eye size={16} />}
                label="Gérer les accès"
                onClick={() => {
                  const doc = desktopActionMenu.item;
                  closeAllActionMenus();
                  openAccess("document", [doc]);
                }}
              />
            )}

            {desktopActionMenu.item.permissions?.canShare && (
              <ActaActionMenuItem
                icon={<ShareNetwork size={16} />}
                label="Partager"
                onClick={() => {
                  const doc = desktopActionMenu.item;
                  closeAllActionMenus();
                  openShare({
                    type: "document",
                    id: doc.id,
                    name: doc.name,
                  });
                }}
              />
            )}

            {desktopActionMenu.item.permissions?.canEdit && (
              <ActaActionMenuItem
                icon={<FolderIcon size={16} />}
                label="Déplacer"
                onClick={() => {
                  const doc = desktopActionMenu.item;
                  closeAllActionMenus();
                  setMoveItem({
                    type: "single",
                    docs: [doc],
                  });
                }}
              />
            )}

            {desktopActionMenu.item.permissions?.canDelete && (
              <ActaActionMenuItem
                icon={<Trash size={16} />}
                label="Supprimer"
                danger
                onClick={() => {
                  const doc = desktopActionMenu.item;
                  closeAllActionMenus();
                  setDeleteTarget({ type: "document", item: doc });
                }}
              />
            )}
          </>
        ) : null}

        {desktopActionMenu?.type === "folder" ? (
          <>
            {desktopActionMenu.item.permissions?.canEdit && (
              <ActaActionMenuItem
                icon={<PencilSimple size={16} />}
                label="Renommer"
                onClick={() => {
                  const folder = desktopActionMenu.item;
                  closeAllActionMenus();
                  setRenameTarget({ type: "folder", item: folder });
                }}
              />
            )}

            {desktopActionMenu.item.permissions?.canManageAccess && (
              <ActaActionMenuItem
                icon={<Eye size={16} />}
                label="Gérer les accès"
                onClick={() => {
                  const folder = desktopActionMenu.item;
                  closeAllActionMenus();
                  openAccess("folder", [folder]);
                }}
              />
            )}

            {desktopActionMenu.item.permissions?.canShare && (
              <ActaActionMenuItem
                icon={<ShareNetwork size={16} />}
                label="Partager"
                onClick={() => {
                  const folder = desktopActionMenu.item;
                  closeAllActionMenus();
                  openShare({
                    type: "folder",
                    id: folder.id,
                    name: folder.name,
                  });
                }}
              />
            )}

            {desktopActionMenu.item.permissions?.canEdit && (
              <ActaActionMenuItem
                icon={<FolderIcon size={16} />}
                label="Déplacer"
                onClick={() => {
                  const folder = desktopActionMenu.item;
                  closeAllActionMenus();
                  setMoveItem({
                    type: "folders",
                    folders: [folder],
                  });
                }}
              />
            )}

            {desktopActionMenu.item.permissions?.canDelete && (
              <ActaActionMenuItem
                icon={<Trash size={16} />}
                label="Supprimer"
                danger
                onClick={() => {
                  const folder = desktopActionMenu.item;
                  closeAllActionMenus();
                  setDeleteTarget({ type: "folder", item: folder });
                }}
              />
            )}
          </>
        ) : null}
      </ActaActionMenu>

      {renameTarget && (
        <RenameItemModal
          target={renameTarget}
          onClose={() => setRenameTarget(null)}
          onRename={renameItem}
        />
      )}

      {preview && <DocumentPreviewModal doc={preview} onClose={() => setPreview(null)} />}


      {shareOpen && shareItem && (
        <ShareModal item={shareItem} onClose={() => { setShareOpen(false); setShareItem(null); }} />
      )}

      {accessSelection && (
        <AccessModal
          items={accessSelection.items}
          type={accessSelection.type}
          onClose={() => setAccessSelection(null)}
          onSaved={() => load()}
        />
      )}

      {moveItem?.type === "folders" && (
        <MoveFoldersModal
          folders={editableFolders}
          foldersToMove={moveItem.folders}
          onClose={() => setMoveItem(null)}
          onMove={moveFolders}
        />
      )}

      {moveItem && moveItem.type !== "folders" && (
        <MoveDocumentsModal
          folders={editableFolders}
          currentFolderId={currentFolderId}
          docs={moveItem.docs}
          onClose={() => setMoveItem(null)}
          onMove={moveDocuments}
        />
      )}
    </div>
  );
}

function RenameItemModal({ target, onClose, onRename }) {
  const [name, setName] = useState(target.item.name || "");
  const [saving, setSaving] = useState(false);
  const label = target.type === "folder" ? "dossier" : "document";

  const submit = async (event) => {
    event.preventDefault();
    const trimmedName = name.trim();

    if (!trimmedName || trimmedName === target.item.name) {
      onClose();
      return;
    }

    setSaving(true);
    try {
      await onRename(target, trimmedName);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="documents-v2-modal-backdrop acta-modal-overlay"
      onClick={saving ? undefined : onClose}
      data-testid={`rename-${target.type}-modal`}
    >
      <form
        className="documents-v2-modal acta-modal-surface"
        onClick={(event) => event.stopPropagation()}
        onSubmit={submit}
        role="dialog"
        aria-modal="true"
        aria-labelledby="rename-item-title"
      >
        <div className="documents-v2-modal-header">
          <div>
            <div className="label-eyebrow">Renommer</div>
            <div id="rename-item-title" className="font-heading font-semibold text-sm mt-1">
              {target.item.name}
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="p-1.5 hover:bg-[var(--do-bg-2)]"
            aria-label="Fermer"
          >
            <X size={16} />
          </button>
        </div>

        <div className="p-5">
          <label htmlFor="rename-item-input" className="label-eyebrow mb-2 block">
            Nouveau nom du {label}
          </label>
          <input
            id="rename-item-input"
            value={name}
            onChange={(event) => setName(event.target.value)}
            className="documents-v2-input w-full"
            autoFocus
            maxLength={255}
            disabled={saving}
            data-testid="rename-item-input"
          />

          <div className="flex justify-end gap-2 mt-5">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="px-3 py-2 text-sm text-[var(--do-muted)]"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={saving || !name.trim() || name.trim() === target.item.name}
              className="documents-v2-primary-button disabled:opacity-50"
              data-testid="rename-item-confirm"
            >
              {saving ? "Renommage…" : "Renommer"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

function MoveFoldersModal({
  folders,
  foldersToMove,
  onClose,
  onMove,
}) {
  const rootFolders = useMemo(() => {
    const selectedIds = new Set(
      foldersToMove.map((folder) => folder.id)
    );

    const byId = new Map(
      folders.map((folder) => [folder.id, folder])
    );

    return foldersToMove.filter((folder) => {
      let parentId = folder.parentId || null;

      while (parentId) {
        if (selectedIds.has(parentId)) {
          return false;
        }

        parentId =
          byId.get(parentId)?.parentId || null;
      }

      return true;
    });
  }, [folders, foldersToMove]);

  const commonParentId = useMemo(() => {
    if (rootFolders.length === 0) {
      return undefined;
    }

    const parentId =
      rootFolders[0].parentId || null;

    return rootFolders.every(
      (folder) =>
        (folder.parentId || null) === parentId
    )
      ? parentId
      : undefined;
  }, [rootFolders]);

  const [targetFolderId, setTargetFolderId] =
    useState(commonParentId);

  const forbiddenTargetIds = useMemo(() => {
    const forbidden = new Set();

    rootFolders.forEach((folder) => {
      collectFolderSubtreeIds(
        folders,
        folder.id
      ).forEach((id) => forbidden.add(id));
    });

    return forbidden;
  }, [folders, rootFolders]);

  const availableFolders = useMemo(
    () =>
      folders.filter(
        (candidate) =>
          !forbiddenTargetIds.has(candidate.id)
      ),
    [folders, forbiddenTargetIds]
  );

  const folderTree = useMemo(
    () => buildFolderTree(availableFolders),
    [availableFolders]
  );

  const destinationNotSelected =
    targetFolderId === undefined;

  const unchanged =
    commonParentId !== undefined &&
    targetFolderId === commonParentId;

  return (
    <div
      className="documents-v2-modal-backdrop acta-modal-overlay"
      onClick={onClose}
      data-testid="move-folders-modal"
    >
      <div
        className="documents-v2-modal acta-modal-surface"
        onClick={(event) =>
          event.stopPropagation()
        }
        role="dialog"
        aria-modal="true"
        aria-label={`Déplacer ${rootFolders.length} dossier(s)`}
      >
        <div className="documents-v2-modal-header">
          <div>
            <div className="label-eyebrow">
              Déplacer
            </div>

            <div className="font-heading font-semibold text-sm mt-1">
              {rootFolders.length} dossier(s)
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="p-1.5 hover:bg-[var(--do-bg-2)]"
            data-testid="move-folders-close"
          >
            <X size={16} />
          </button>
        </div>

        <div className="p-5">
          <div className="label-eyebrow mb-2">
            Destination
          </div>

          <div className="documents-v2-move-tree">
            <button
              type="button"
              onClick={() =>
                setTargetFolderId(null)
              }
              className={`w-full flex items-center gap-2 px-2 py-2 rounded-lg text-left text-sm ${
                targetFolderId === null
                  ? "bg-[var(--do-accent-soft)] text-[var(--lirah-primary)]"
                  : "hover:bg-[var(--do-bg-solid)]"
              }`}
              data-testid="move-folders-target-root"
            >
              <FolderOpen
                size={16}
                weight="duotone"
              />
              Tous les documents
            </button>

            {folderTree.map((folder) => (
              <MoveFolderOption
                key={folder.id}
                folder={folder}
                level={1}
                targetFolderId={targetFolderId}
                onSelect={setTargetFolderId}
              />
            ))}
          </div>

          <div className="flex justify-end gap-2 mt-5">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-2 text-sm text-[var(--do-muted)]"
            >
              Annuler
            </button>

            <button
              type="button"
              onClick={() =>
                onMove(
                  rootFolders,
                  targetFolderId
                )
              }
              disabled={
                rootFolders.length === 0 ||
                destinationNotSelected ||
                unchanged
              }
              className="documents-v2-primary-button disabled:opacity-50"
              data-testid="move-folders-confirm"
            >
              Déplacer ici
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function MoveDocumentsModal({ folders, currentFolderId, docs, onClose, onMove }) {
  const [targetFolderId, setTargetFolderId] = useState(currentFolderId || null);
  const folderTree = useMemo(() => buildFolderTree(folders), [folders]);

  return (
    <div
      className="documents-v2-modal-backdrop acta-modal-overlay"
      onClick={onClose}
      data-testid="move-documents-modal"
    >
      <div
        className="documents-v2-modal acta-modal-surface"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={`Déplacer ${docs.length} document(s)`}
      >
        <div className="documents-v2-modal-header">
          <div>
            <div className="label-eyebrow">Déplacer</div>
            <div className="font-heading font-semibold text-sm mt-1">
              {docs.length} document(s)
            </div>
          </div>

          <button onClick={onClose} className="p-1.5 hover:bg-[var(--do-bg-2)]" data-testid="move-close">
            <X size={16} />
          </button>
        </div>

        <div className="p-5">
          <div className="label-eyebrow mb-2">Destination</div>

          <div className="documents-v2-move-tree">
            <button
              onClick={() => setTargetFolderId(null)}
              className={`w-full flex items-center gap-2 px-2 py-2 rounded-lg text-left text-sm ${
                targetFolderId === null ? "bg-[var(--do-accent-soft)] text-[var(--lirah-primary)]" : "hover:bg-[var(--do-bg-solid)]"
              }`}
              data-testid="move-target-root"
            >
              <FolderOpen size={16} weight="duotone" />
              Tous les documents
            </button>

            {folderTree.map((folder) => (
              <MoveFolderOption
                key={folder.id}
                folder={folder}
                level={1}
                targetFolderId={targetFolderId}
                onSelect={setTargetFolderId}
              />
            ))}
          </div>

          <div className="flex justify-end gap-2 mt-5">
            <button onClick={onClose} className="px-3 py-2 text-sm text-[var(--do-muted)]">
              Annuler
            </button>

            <button
              onClick={() => onMove(docs, targetFolderId)}
              className="documents-v2-primary-button"
              data-testid="move-confirm"
            >
              Déplacer ici
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function MoveFolderOption({ folder, level, targetFolderId, onSelect }) {
  const isSelected = targetFolderId === folder.id;

  return (
    <div>
      <button
        onClick={() => onSelect(folder.id)}
        className={`w-full flex items-center gap-2 px-2 py-2 rounded-lg text-left text-sm ${
          isSelected ? "bg-[var(--do-accent-soft)] text-[var(--lirah-primary)]" : "hover:bg-[var(--do-bg-solid)]"
        }`}
        style={{ paddingLeft: `${8 + level * 14}px` }}
        data-testid={`move-target-${folder.id}`}
      >
        <FolderIcon size={16} weight={isSelected ? "fill" : "duotone"} />
        <span className="truncate">{folder.name}</span>
      </button>

      {folder.children.map((child) => (
        <MoveFolderOption
          key={child.id}
          folder={child}
          level={level + 1}
          targetFolderId={targetFolderId}
          onSelect={onSelect}
        />
      ))}
    </div>
  );
}
